import { useEffect, useState } from 'react';
import { useT } from '@/i18n';
import { Check } from '@/components/ui';
import { cx } from '@/components/ui/cx';
import { profileSteps, type AboutState } from './aboutForm';

/**
 * How much of the "about my child" profile is filled in: a bar that grows as the parent answers,
 * with a light sweeping along it, and a check that pops in once everything is answered. The fill
 * starts at zero and grows to its value on mount so the first view animates too.
 */
export const ProfileProgress = ({ state, childName }: { state: AboutState; childName: string }) => {
  const t = useT();
  const { done, total } = profileSteps(state);
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const complete = done === total;
  const left = total - done;

  const [shown, setShown] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(pct));
    return () => cancelAnimationFrame(id);
  }, [pct]);

  return (
    <div className="sticky top-[80px] z-10 rounded-lg border border-line bg-white/95 p-4 shadow-1 backdrop-blur">
      <div className="flex items-center justify-between gap-3">
        <p className="text-label text-ink" aria-hidden="true">
          {t('about.progress.title', { pct })}
        </p>
        <span
          aria-hidden="true"
          className={cx(
            'inline-flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-caption font-extrabold tabular-nums transition-colors duration-gentle',
            complete ? 'bg-sage-500 text-white' : 'bg-lagoon-100 text-lagoon-700',
          )}
        >
          {complete ? <Check key="done" className="h-4 w-4 animate-pop" /> : `${done}/${total}`}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={t('about.progress.title', { pct })}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        className="relative mt-3 h-2.5 overflow-hidden rounded-pill bg-mist"
      >
        <div
          className={cx(
            'relative h-full overflow-hidden rounded-pill transition-[width] duration-sheet ease-gentle',
            complete ? 'bg-gradient-to-r from-sage-500 to-lagoon-500' : 'bg-gradient-to-r from-lagoon-500 to-sage-500',
          )}
          style={{ width: `${shown}%` }}
        >
          {shown > 0 && <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1/3 animate-shimmer bg-gradient-to-r from-transparent via-white/50 to-transparent" />}
        </div>
      </div>
      <p className="mt-2 text-caption text-ink-muted" aria-live="polite">
        {complete
          ? t('about.progress.done', { child: childName })
          : t(left === 1 ? 'about.progress.leftOne' : 'about.progress.left', { n: left, child: childName })}
      </p>
    </div>
  );
};
